import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { MessageCircle, CheckCircle, Instagram, Facebook, ChevronDown, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import GalleryModal from '../components/GalleryModal';
import Reviews from '../components/Reviews';
import SEOHead, { SITE_URL } from '../components/SEOHead';
import HeroBackgroundVideo from '../components/HeroBackgroundVideo';
import SafeImage from '../components/SafeImage';
import TrainingMethodModal from '../components/TrainingMethodModal';
import { resolveInstagramUrl } from '../constants/social';
import { useCalendlyEmbed } from '../context/CalendlyEmbedContext';
import { pickTwoPackages } from '../utils/landingPackages';
import { sortTrainingMethodsForModal } from '../utils/trainingMethods';
import './LandingPage.css';

function MobilePage() {
  const { openCalendly } = useCalendlyEmbed();
  const [hero, setHero] = useState({ title: '', subtitle: '', buttonText: '' });
  const [youtubeVideo, setYoutubeVideo] = useState({ url: '', title: '' });
  const [whoFor, setWhoFor] = useState({ title: '', content: '', imageUrl: '' });
  const [packages, setPackages] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [about, setAbout] = useState({ title: 'Meet Carlo', content: '', certifications: [], imageUrl: '' });
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [finalCTA, setFinalCTA] = useState({ title: '', subtitle: '', calendlyUrl: '' });
  const [contact, setContact] = useState({ title: 'Ready to Start?', subtitle: '' });
  const [growthWays, setGrowthWays] = useState([]);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const galleryScrollRef = useRef(null);
  const [instagramUrl, setInstagramUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  useEffect(() => {
    const openContact = () => setShowContactModal(true);
    const openTraining = () => setShowTrainingModal(true);
    window.addEventListener('bodyworks-open-contact', openContact);
    window.addEventListener('bodyworks-open-training', openTraining);
    return () => {
      window.removeEventListener('bodyworks-open-contact', openContact);
      window.removeEventListener('bodyworks-open-training', openTraining);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [
        heroDoc,
        growthSnapshot,
        contactDoc,
        videoDoc,
        whoForDoc,
        packagesSnapshot,
        aboutDoc,
        trainersSnapshot,
        gallerySnapshot,
        footerDoc,
        reviewsSnapshot,
        ctaDoc,
      ] = await Promise.all([
        getDoc(doc(db, 'settings', 'mobileHero')),
        getDocs(collection(db, 'growthWays')),
        getDoc(doc(db, 'settings', 'contact')),
        getDoc(doc(db, 'settings', 'mobileVideo')),
        getDoc(doc(db, 'settings', 'mobileWhoFor')),
        getDocs(collection(db, 'mobilePackages')),
        getDoc(doc(db, 'settings', 'about')),
        getDocs(collection(db, 'trainers')),
        getDocs(collection(db, 'mobileGallery')),
        getDoc(doc(db, 'settings', 'footer')),
        getDocs(collection(db, 'reviews')),
        getDoc(doc(db, 'settings', 'mobileFinalCTA')),
      ]);

      if (heroDoc.exists()) setHero(heroDoc.data());

      setGrowthWays(
        growthSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      );

      if (contactDoc.exists()) setContact(contactDoc.data());
      if (videoDoc.exists()) setYoutubeVideo(videoDoc.data());
      if (whoForDoc.exists()) setWhoFor(whoForDoc.data());

      setPackages(
        packagesSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      );

      if (aboutDoc.exists()) setAbout(aboutDoc.data());
      setTrainers(trainersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGallery(gallerySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      if (footerDoc.exists()) {
        setInstagramUrl(resolveInstagramUrl(footerDoc.data().instagramUrl));
        if (footerDoc.data().tiktokUrl) setTiktokUrl(footerDoc.data().tiktokUrl);
        if (footerDoc.data().facebookUrl) setFacebookUrl(footerDoc.data().facebookUrl);
      } else {
        setInstagramUrl(resolveInstagramUrl());
      }

      setReviews(
        reviewsSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((review) => review.active !== false)
          .sort((a, b) => (b.order || 0) - (a.order || 0))
      );

      if (ctaDoc.exists()) setFinalCTA(ctaDoc.data());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const scrollToPackages = () => {
    const element = document.getElementById('packages');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const displayPackages = pickTwoPackages(packages);
  const trainingMethods = sortTrainingMethodsForModal(growthWays);

  const openTrainingModal = () => setShowTrainingModal(true);

  return (
    <div className="landing-page">
      <SEOHead
        title="Mobile Personal Training Palmetto Bay, Pinecrest & Kendall | BodyWorks Training"
        description="Mobile personal training at your home or location in Palmetto Bay, Pinecrest, Kendall & nearby Miami-Dade. Equipped sessions — your personal trainer comes to you."
        canonical={`${SITE_URL}/mobile`}
        keywords="mobile personal training Palmetto Bay, mobile personal training Pinecrest, mobile personal training Kendall, personal trainer near me, equipped mobile training, BodyWorks Training, Bodyworks Training LLC, at home personal trainer Miami"
      />

      <button type="button" className="floating-trial-btn" onClick={openTrainingModal}>
        Free Trial Session
      </button>

      {/* Floating contact — curved label + bubble (matches Home) */}
      <div className="floating-contact-wrap">
        <svg className="floating-contact-curve" viewBox="-6 -6 124 36" aria-hidden="true">
          <defs>
            <path
              id="floating-contact-arc-mobile"
              d="M 10,26 A 48,48 0 0,1 102,26"
              fill="none"
            />
          </defs>
          <text className="floating-contact-curve-text" textAnchor="middle">
            <textPath href="#floating-contact-arc-mobile" startOffset="50%">
              WhatsApp · Text · Call
            </textPath>
          </text>
        </svg>
        <button type="button" onClick={() => setShowContactModal(true)} className="floating-contact-btn" aria-label="Contact us — WhatsApp or call">
          <MessageCircle size={24} />
        </button>
      </div>

      {/* Hero Section */}
      <section className="landing-hero">
        <HeroBackgroundVideo videoUrl={hero.backgroundVideoUrl} />
        <div className="landing-hero-content">
          <h1 className="landing-hero-title">{hero.title}</h1>
          <p className="landing-hero-subtitle">{hero.subtitle}</p>
          {hero.buttonText && (
            <button type="button" onClick={scrollToPackages} className="cta-button">
              {hero.buttonText}
            </button>
          )}
        </div>
        <button 
          onClick={scrollToPackages}
          className="landing-scroll-arrow"
          aria-label="Scroll down"
        >
          <span style={{fontSize: '0.85rem', fontFamily: 'Antonio, sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.25rem'}}>Scroll Down</span>
          <ChevronDown size={32} />
        </button>
      </section>

      {youtubeVideo && youtubeVideo.url && (
        <section className="video-section">
          <div className="video-container">
            {youtubeVideo.title && <h2 className="section-title">{youtubeVideo.title}</h2>}
            <div className="video-wrapper">
              <iframe
                className="youtube-iframe"
                src={`https://www.youtube.com/embed/${youtubeVideo.url.includes('watch?v=') ? youtubeVideo.url.split('watch?v=')[1].split('&')[0] : youtubeVideo.url.split('/').pop()}`}
                title={youtubeVideo.title || 'Mobile Training Video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      <TrainingMethodModal
        isOpen={showTrainingModal}
        onClose={() => setShowTrainingModal(false)}
        methods={trainingMethods}
        onBook={(bookingUrl) => {
          setShowTrainingModal(false);
          openCalendly(bookingUrl);
        }}
      />

      {/* Packages Section */}
      <section className="landing-section" id="packages">
        <h2 className="landing-section-title centered">Training Packages</h2>
        <p className="landing-section-subtitle">Choose the plan that fits your goals</p>
        <div className="landing-packages-grid landing-packages-grid--two">
          {displayPackages.length > 0 ? displayPackages.map((pkg, index) => (
            <div key={pkg.id || index} className="landing-package-card">
              <h3 className="landing-package-title">{pkg.title}</h3>
              {pkg.price && <div className="landing-package-price">{pkg.price}</div>}
              <p className="landing-package-description">{pkg.description}</p>
              {pkg.features && pkg.features.length > 0 && (
                <ul className="landing-package-features">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              )}
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem'}}>
                {pkg.calendlyUrl ? (
                  <button
                    type="button"
                    className="cta-button cta-button--static"
                    style={{ width: '100%', textAlign: 'center' }}
                    onClick={() => openCalendly(pkg.calendlyUrl)}
                  >
                    <Calendar size={18} />
                    <span>Get Started</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cta-button cta-button--static"
                    style={{ width: '100%', textAlign: 'center' }}
                    onClick={openTrainingModal}
                  >
                    Get Started
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowContactModal(true)}
                  className="cta-button-secondary"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <MessageCircle size={18} />
                  <span>Questions?</span>
                </button>
              </div>
            </div>
          )) : (
            <p style={{textAlign: 'center', color: 'rgba(245, 245, 245, 0.6)', gridColumn: '1 / -1'}}>
              No packages available at this time.
            </p>
          )}
        </div>
      </section>

      {/* Who It's For Section */}
      {whoFor && (
        <section className="landing-section">
          <div className="landing-two-col">
            <div className="landing-image-container">
              {whoFor.imageUrl ? (
                <SafeImage src={whoFor.imageUrl} alt={whoFor.title} />
              ) : (
                <div className="landing-placeholder">Image</div>
              )}
            </div>
            <div className="landing-text-content">
              <h2 className="landing-section-title">{whoFor.title || 'Who Is This For?'}</h2>
              <p className="landing-description">{whoFor.content}</p>
              
              {whoFor.features && whoFor.features.length > 0 && (
                <div className="landing-features-list" style={{marginTop: '2rem'}}>
                  {whoFor.features.map((feature, index) => (
                    <div key={index} className="landing-feature-item" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'rgba(255, 0, 0, 0.03)',
                      borderLeft: '3px solid var(--primary)',
                      marginBottom: '0.75rem',
                      transition: 'all 0.3s ease'
                    }}>
                      <CheckCircle size={20} color="#888888" style={{flexShrink: 0}} />
                      <span style={{color: 'rgba(245, 245, 245, 0.9)', fontSize: '1rem'}}>{feature}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Trainers Section */}
      {trainers.length > 0 && (
        <section className="trainers-section" id="trainers">
          <h2 className="landing-section-title centered">Trainers</h2>
          <div className="trainers-grid">
            {trainers.map((trainer) => (
              <div key={trainer.id} className="trainer-card">
                {trainer.experience && <div className="trainer-experience">{trainer.experience}</div>}
                <div className="trainer-image-container">
                  <SafeImage
                    src={trainer.imageUrl}
                    alt={trainer.name}
                    className="trainer-image"
                    fallback={
                      <div className="trainer-image-placeholder">
                        <span>{trainer.name?.charAt(0) || 'T'}</span>
                      </div>
                    }
                  />
                </div>

                <div className="trainer-info">
                  <h3 className="trainer-name">{trainer.name}</h3>
                  <p className="trainer-specialty">{trainer.specialty}</p>

                  {trainer.bio && (
                    <p className="trainer-bio">{trainer.bio}</p>
                  )}

                  {trainer.certifications && trainer.certifications.length > 0 && (
                    <div className="trainer-certifications">
                      {(Array.isArray(trainer.certifications) ? trainer.certifications : trainer.certifications.split(',')).map((cert, index) => (
                        <span key={index} className="cert-badge-small">{typeof cert === 'string' ? cert.trim() : cert}</span>
                      ))}
                    </div>
                  )}

                  {trainer.calendlyUrl && (
                    <button
                      type="button"
                      className="trainer-book-btn"
                      onClick={() => openCalendly(trainer.calendlyUrl)}
                    >
                      <Calendar size={18} />
                      <span>Book Session</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <Reviews reviews={reviews} />

      {/* BodyWorks — company story (settings/about) */}
      <section className="about" id="bodyworks">
        <div className="about-content">
          <div className="about-image">
            {about.imageUrl ? (
              <SafeImage
                src={about.imageUrl}
                alt={about.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)'}}>
                Photo
              </div>
            )}
          </div>
          <div className="about-text">
            <h2 className="section-title">BodyWorks</h2>
            <h3 style={{color: 'var(--primary)', marginBottom: '1rem'}}>{about.title}</h3>
            {about.content && about.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
            {!about.content && (
              <>
                <p>With over a decade of experience transforming lives through fitness, I've helped hundreds of clients achieve goals they never thought possible.</p>
                <p>Whether you're looking to lose weight, build muscle, improve athletic performance, or simply feel better in your daily life, I'll create a customized plan that fits your unique needs and challenges.</p>
              </>
            )}
            <div className="certifications">
              {about.certifications && about.certifications.length > 0 ? (
                about.certifications.map((cert, idx) => (
                  <span key={idx} className="cert-badge">{cert}</span>
                ))
              ) : (
                <>
                  <span className="cert-badge">NASM-CPT</span>
                  <span className="cert-badge">Nutrition Specialist</span>
                  <span className="cert-badge">Strength Coach</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="landing-section">
          <h2 className="landing-section-title centered">Gallery</h2>
          <div className="gallery-carousel-wrapper">
            {gallery.length > 4 && (
              <button 
                className="gallery-carousel-arrow gallery-carousel-left"
                onClick={() => {
                  if (galleryScrollRef.current) {
                    galleryScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll left"
              >
                <ChevronLeft size={32} />
              </button>
            )}
            <div className="gallery-carousel" ref={galleryScrollRef}>
              {gallery.map((item, index) => (
                <div 
                  key={item.id || index} 
                  className="gallery-carousel-item"
                  onClick={() => {
                    setCurrentGalleryIndex(index);
                    setShowGalleryModal(true);
                  }}
                >
                  <SafeImage src={item.imageUrl} alt={`Gallery ${index + 1}`} className="gallery-carousel-image" />
                </div>
              ))}
            </div>
            {gallery.length > 4 && (
              <button 
                className="gallery-carousel-arrow gallery-carousel-right"
                onClick={() => {
                  if (galleryScrollRef.current) {
                    galleryScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll right"
              >
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </section>
      )}

      {/* Social Media Links */}
      {(instagramUrl || tiktokUrl || facebookUrl) && (
        <section className="landing-section" style={{textAlign: 'center', paddingTop: '2rem', paddingBottom: '2rem'}}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', alignItems: 'center'}}>
            {instagramUrl && (
              <a 
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Instagram size={24} />
                Follow Us on Instagram
              </a>
            )}
            {tiktokUrl && (
              <a 
                href={tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                Follow Us on TikTok
              </a>
            )}
            {facebookUrl && (
              <a 
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Facebook size={24} />
                Follow Us on Facebook
              </a>
            )}
          </div>
        </section>
      )}

      <section className="contact" id="contact">
        <div className="contact-content">
          <h2>{finalCTA.title || contact.title || 'Ready to Start?'}</h2>
          <p>
            {finalCTA.subtitle ||
              contact.subtitle ||
              'Take the first step towards your transformation. Fill out the form below or send us a message.'}
          </p>
          <div className="contact-buttons">
            <button type="button" onClick={() => setShowContactModal(true)} className="cta-button cta-button--static">
              <MessageCircle size={20} />
              <span>Send Message</span>
            </button>
            <button type="button" onClick={openTrainingModal} className="cta-button-secondary">
              <Calendar size={20} />
              <span>Book Appointment</span>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        images={gallery}
        currentIndex={currentGalleryIndex}
        onNavigate={setCurrentGalleryIndex}
      />
    </div>
  );
}

export default MobilePage;
